/**
 * Role Management Utilities
 *
 * This module provides constants and utilities for managing user roles,
 * supporting both new and legacy role types during the migration period.
 */

// Role type definitions
export enum RoleType {
  // Current roles
  PRACTICE_ADMINISTRATOR = 'practice_administrator',
  BILLING_ADMINISTRATOR = 'billing_administrator',
  CLINICIAN = 'clinician',
  OPERATIONS_SUPPORT = 'operations_support',
  SUPERVISOR = 'supervisor',

  // Legacy roles (for backward compatibility)
  ADMIN = 'admin',
  MEMBER = 'member',
  BILLING_ADMIN = 'billing_admin',
}

// Role constants for string-based usage
export const ROLES = {
  // Current roles
  PRACTICE_ADMINISTRATOR: 'practice_administrator' as const,
  BILLING_ADMINISTRATOR: 'billing_administrator' as const,
  CLINICIAN: 'clinician' as const,
  OPERATIONS_SUPPORT: 'operations_support' as const,
  SUPERVISOR: 'supervisor' as const,

  // Legacy roles
  ADMIN: 'admin' as const,
  MEMBER: 'member' as const,
  BILLING_ADMIN: 'billing_admin' as const,
} as const;

// All valid roles
export const VALID_ROLES = new Set<string>(Object.values(ROLES));

// Role classifications
export const BILLABLE_ROLES = new Set(['clinician']);

export const ADMIN_EQUIVALENT_ROLES = new Set([
  'admin', // Legacy
  'operations_support',
  'practice_administrator',
  'supervisor',
]);

export const MEMBER_EQUIVALENT_ROLES = new Set([
  'member', // Legacy
  'clinician',
]);

export const BILLING_ROLES = new Set(['practice_administrator']);

// For API compatibility - maps roles to their equivalents
export const API_COMPATIBILITY_MAPPING: Record<string, string[]> = {
  // New roles
  clinician: ['member', 'clinician'],
  operations_support: ['admin', 'operations_support'],
  practice_administrator: ['admin', 'practice_administrator'],
  supervisor: ['admin', 'supervisor'],
  billing_administrator: ['billing_admin', 'billing_administrator'],

  // Legacy mappings (when stored role is still old)
  member: ['member', 'clinician'],
  admin: ['admin', 'operations_support'],
  billing_admin: ['billing_admin', 'billing_administrator'],
};

// Display names for UI
export const ROLE_DISPLAY_NAMES: Record<string, string> = {
  // Current roles
  practice_administrator: 'Practice Administrator',
  billing_administrator: 'Billing Administrator',
  clinician: 'Clinician',
  operations_support: 'Operations Support',
  supervisor: 'Supervisor',

  // Legacy roles
  admin: 'View Only',
  member: 'Provider',
  billing_admin: 'Billing Admin',
};

// Helper type for role strings
export type Role = keyof typeof ROLES | (string & {});

/**
 * Check if user has admin privileges (any admin-equivalent role)
 */
export function hasAdminPrivileges(roles: string[] | undefined): boolean {
  if (!roles || roles.length === 0) return false;
  return roles.some(role => ADMIN_EQUIVALENT_ROLES.has(role));
}

/**
 * Check if user has billing access
 */
export function hasBillingAccess(roles: string[] | undefined): boolean {
  if (!roles || roles.length === 0) return false;
  return roles.some(role => BILLING_ROLES.has(role));
}

/**
 * Check if user has provider/clinician access
 */
export function hasProviderAccess(roles: string[] | undefined): boolean {
  if (!roles || roles.length === 0) return false;
  return roles.some(role => MEMBER_EQUIVALENT_ROLES.has(role));
}

/**
 * Check if user is billable (can generate billable notes)
 */
export function isBillableUser(roles: string[] | undefined): boolean {
  if (!roles || roles.length === 0) return false;
  return roles.some(role => BILLABLE_ROLES.has(role));
}

/**
 * Check if a role is a legacy role that needs migration
 */
export function isLegacyRole(role: string): boolean {
  return role === 'admin' || role === 'member' || role === 'billing_admin';
}

/**
 * Get display name for a role
 */
export function getRoleDisplayName(role: string): string {
  return ROLE_DISPLAY_NAMES[role] || role;
}

/**
 * Get the primary new role for a legacy role
 */
export function getLegacyRoleMapping(legacyRole: string): string {
  switch (legacyRole) {
    case 'admin':
      return 'operations_support';
    case 'member':
      return 'clinician';
    case 'billing_admin':
      return 'billing_administrator';
    default:
      return legacyRole;
  }
}

/**
 * Check if user has a specific role (supports both new and legacy)
 */
export function hasRole(roles: string[] | undefined, roleToCheck: string): boolean {
  if (!roles || roles.length === 0) return false;

  // Direct match
  if (roles.includes(roleToCheck)) return true;

  // Check equivalent roles
  switch (roleToCheck) {
    case 'admin':
      return hasAdminPrivileges(roles);
    case 'member':
      return hasProviderAccess(roles);
    case 'billing_admin':
      return hasBillingAccess(roles);
    default:
      return roles.includes(roleToCheck);
  }
}

/**
 * Check if user has any of the specified roles
 */
export function hasAnyRole(userRoles: string[] | undefined, rolesToCheck: string[]): boolean {
  if (!userRoles || userRoles.length === 0) return false;
  return rolesToCheck.some(role => hasRole(userRoles, role));
}

/**
 * Check if user has all of the specified roles
 */
export function hasAllRoles(userRoles: string[] | undefined, rolesToCheck: string[]): boolean {
  if (!userRoles || userRoles.length === 0) return false;
  return rolesToCheck.every(role => hasRole(userRoles, role));
}

/**
 * Get all equivalent roles for a given role (for compatibility)
 */
export function getEquivalentRoles(role: string): string[] {
  return API_COMPATIBILITY_MAPPING[role] || [role];
}

/**
 * Check if user is view-only admin (has ONLY admin role, no provider access)
 */
export function isViewOnlyAdmin(roles: string[] | undefined): boolean {
  if (!roles || roles.length === 0) return false;

  // Legacy check: only has 'admin' role
  if (roles.length === 1 && roles[0] === 'admin') return true;

  // New check: has admin privileges but no provider access
  return hasAdminPrivileges(roles) && !hasProviderAccess(roles);
}

/**
 * Sort roles for consistent display order
 */
export function sortRoles(roles: string[]): string[] {
  const roleOrder = [
    'practice_administrator',
    'billing_administrator',
    'supervisor',
    'clinician',
    'operations_support',
    'billing_admin', // Legacy
    'admin', // Legacy
    'member', // Legacy
  ];

  return [...roles].sort((a, b) => {
    const indexA = roleOrder.indexOf(a);
    const indexB = roleOrder.indexOf(b);

    if (indexA === -1 && indexB === -1) return a.localeCompare(b);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });
}

/**
 * Validate if a role string is valid
 */
export function isValidRole(role: string): boolean {
  return VALID_ROLES.has(role);
}

/**
 * Get role badge color for UI display
 */
export function getRoleBadgeColor(
  role: string,
): 'primary' | 'secondary' | 'default' | 'error' | 'warning' | 'info' | 'success' {
  if (BILLING_ROLES.has(role)) return 'primary';
  if (ADMIN_EQUIVALENT_ROLES.has(role)) return 'secondary';
  if (MEMBER_EQUIVALENT_ROLES.has(role)) return 'success';
  return 'default';
}
